import React, { useEffect, useState, useCallback, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Tooltip,
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

//
// HELPER COMPONENTS FOR TABLE DISPLAY
//

// Regular row with label and value
const Row = ({ label, value }) => (
  <tr style={{ background: "white" }}>
    <td
      style={{
        width: "30%",
        padding: "6px",
        border: "1px solid #ddd",
        fontWeight: 600,
        verticalAlign: "top",
      }}
    >
      {label}
    </td>
    <td
      style={{
        width: "70%",
        padding: "6px",
        border: "1px solid #ddd",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        verticalAlign: "top",
      }}
    >
      {value ?? "—"}
    </td>
  </tr>
);

// Section header row
const Section = ({ title }) => (
  <tr>
    <td
      colSpan={2}
      style={{
        padding: "8px",
        border: "1px solid #ddd",
        fontWeight: "bold",
        color: "#000",
        background: "#f5f5f5",
      }}
    >
      {title}
    </td>
  </tr>
);

//
// UTILITY FUNCTIONS (Robust Date & Data Handling)
//

// 1. Robust Date Parser
const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === "number") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(val).trim();

  // ISO: 2025-10-01 or 2025-10-01T06:21:32.433
  let m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (m) {
    const yyyy = +m[1],
      mm = +m[2] - 1,
      dd = +m[3];
    const hh = +(m[4] || 0),
      min = +(m[5] || 0),
      sec = +(m[6] || 0);
    return new Date(yyyy, mm, dd, hh, min, sec);
  }

  // US: MM/DD/YYYY [hh:mm AM/PM]
  m = s.match(
    /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i
  );
  if (m) {
    let mm = +m[1],
      dd = +m[2],
      yyyy = +m[3];
    if (yyyy < 100) yyyy += 2000;
    let hh = +(m[4] || 0),
      min = +(m[5] || 0);
    const ap = (m[6] || "").toUpperCase();
    if (ap === "PM" && hh < 12) hh += 12;
    if (ap === "AM" && hh === 12) hh = 0;
    return new Date(yyyy, mm - 1, dd, hh, min, 0);
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

// 2. Format date to MM/DD/YYYY
const fmtDate = (val) => {
  const d = toDate(val);
  if (!d) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

// 3. Format a datetime to local string
const fmtDateTime = (val) => {
  const d = toDate(val);
  if (!d) return "—";
  return d.toLocaleString();
};

// 4. Get first non-empty value
const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      const s = typeof v === "string" ? v.trim() : String(v);
      if (s !== "") return v;
    }
  }
  return "";
};

// 5. Convert value to boolean
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string")
    return ["true", "yes", "y", "1"].includes(v.trim().toLowerCase());
  return !!v;
};

// 6. Convert boolean to Yes/No
const formatBool = (v) => (toBool(v) ? "Yes" : "No");

//
// MAIN COMPONENT
//

const ClientSessionVerificationPreview = ({ data, onClose }) => {
  // State
  const [form, setForm] = useState({});
  const [session, setSession] = useState({});
  const [patientDocumentUrl, setPatientDocumentUrl] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Signature state
  const [patientSigLink, setPatientSigLink] = useState("");
  const sigCanvasRef = useRef(null);
  const [openClientSignatureModal, setOpenClientSignatureModal] = useState(false);
  
  // Removed showSuccessAlert state as we are using Swal now

  // Get the PatientFormId
  const PatientFormId = firstNonEmpty(
    data?.PatientFormId,
    data?.patientFormId,
    data?.patientformId,
    data?.id,
    data?.Id
  );

  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "https://neuroez4.devforhealth.com/api";

  // ========== Helpers for Fetching ==========

  // Safe JSON Fetch
  const fetchJSONSafe = async (url, options = {}) => {
    const res = await fetch(url, {
      headers: { Accept: "application/json", ...(options.headers || {}) },
      ...options,
    });
    let data = {};
    if (res.status !== 204) {
      const text = await res.text();
      if (text && text.trim()) {
        try {
          data = JSON.parse(text);
        } catch {
          data = {};
        }
      }
    }
    return { ok: res.ok, status: res.status, data };
  };

  // Get Image Blob
  const getClientImage = async (path, token) => {
    try {
      if (!path) return "";
      if (/^(https?:|data:)/i.test(path)) return path;

      const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
        path
      )}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return "";

      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("text/plain")) {
        const link = await res.text();
        return link || "";
      }

      const blob = await res.blob();
      if (blob && blob.size > 0) {
        return URL.createObjectURL(blob);
      }
      return "";
    } catch {
      return "";
    }
  };

  // ========== Modal & Signature Handlers ==========

  const openClientModal = () => setOpenClientSignatureModal(true);

  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;
    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setPatientSigLink("");

        try {
          const token = localStorage.getItem("neuro_patientportal_auth_token");
          if (!token) throw new Error("Auth token missing");

          const client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          // Send full form meta with cleared signature to API
          const payload = {
            ...form,
            Id: form.Id ?? form.id ?? PatientFormId,
            patientFormId: PatientFormId,
            clientsBase64: "",
            clientsURL: "",
          };

          await client.post("/Forms/AddPatientForm", payload);

          Swal.fire({
            title: "Deleted!",
            text: "Signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const el = document.querySelector(".swal2-container");
              if (el) el.style.zIndex = "9999";
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const el = document.querySelector(".swal2-container");
              if (el) el.style.zIndex = "9999";
            },
          });
        }
      }
    });
  };

  const handleSaveForm = async () => {
    if (!PatientFormId) return;

    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      if (!token) throw new Error("Auth token missing");

      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      // Determine if image is new Base64 or existing URL
      const isBase64 = patientSigLink && patientSigLink.toString().includes("base64");

      const payload = {
        ...form,
        Id: form.Id ?? form.id ?? PatientFormId,
        patientFormId: PatientFormId,
        clientsBase64: isBase64 ? patientSigLink : "",
        clientsURL: isBase64 ? "" : (patientSigLink || ""),
      };

      await client.post("/Forms/AddPatientForm", payload);

      // --- Updated SweetAlert Success Popup ---
      Swal.fire({
        title: "Form Saved",
        text: "Form saved successfully",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        didOpen: () => {
          // Ensure popup is above modal
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      }).then(() => {
        // Logic to close the preview form after clicking OK
        if (onClose) {
          onClose();
        } else {
          try {
            const backdrop = document.querySelector(".MuiBackdrop-root");
            if (backdrop) {
              backdrop.click();
            } else {
              const closeButton = document.querySelector('[aria-label="close"]');
              if (closeButton) {
                closeButton.click();
              } else {
                window.history.back();
              }
            }
          } catch (err) {
            console.error("Could not close form:", err);
            window.location.reload();
          }
        }
      });

    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "9999";
        },
      });
    }
  };

  // ========== Data Loading ==========
  useEffect(() => {
    let active = true;
    if (!PatientFormId) {
      setLoading(false);
      setError("No Patient Form ID provided");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        if (!token) throw new Error("Authentication token not found.");
        const headers = { Authorization: `Bearer ${token}` };

        // 1. Fetch Form and Session Data
        const [formRes, sessionRes] = await Promise.all([
          fetchJSONSafe(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${PatientFormId}`,
            { headers }
          ),
          fetchJSONSafe(
            `${API_BASE_URL}/ClientSessionVerification/getClientSessionVerificationByFormId?patientFormId=${PatientFormId}`,
            { headers }
          ),
        ]);

        if (!active) return;

        const formData = Array.isArray(formRes.data)
          ? formRes.data[0] || {}
          : formRes.data || {};
        const sessionData = sessionRes.data || {};

        setForm(formData);
        setSession(sessionData);

        // 2. Fetch Attached Document Preview Link (if any)
        if (formData.patientDocumentURL) {
          try {
            const docUrl = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
              formData.patientDocumentURL
            )}`;
            const res = await fetch(docUrl, { headers });
            if (res.ok) {
              const txt = await res.text();
              if (active) setPatientDocumentUrl(txt);
            }
          } catch (e) {
            console.error("Doc fetch error", e);
          }
        }

        // 3. Fetch Client Signature Image
        const clientPath = firstNonEmpty(
          formData.clientsURL,
          formData.ClientsURL,
          formData.clientSignatureURL,
          formData.patientSignatureURL
        );

        const img = await getClientImage(clientPath, token);
        if (active && img) {
          setPatientSigLink(img);
        }
      } catch (err) {
        if (active) {
          console.error(err);
          setError(err.message || "Failed to load data.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();
    return () => {
      active = false;
    };
  }, [PatientFormId, API_BASE_URL]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  // ========== Derived Values for Rendering ==========

  const headerTitle = "Client Session Verification";

  const dosVal = fmtDate(firstNonEmpty(session.assessmentDate, form.assessmentDate));
  const fileNo = firstNonEmpty(form.accountNo, session.accountNo, form.patientAccount) || "—";
  const clientName = firstNonEmpty(form.patientName, form.PatientNameclient, session.patientName, session.PatientNameclient) || "—";
  const dobVal = fmtDate(firstNonEmpty(form.dob, form.dateOfBirth));

  const assessmentDate = fmtDate(firstNonEmpty(session.assessmentDate, form.assessmentDate));
  const assessmentDateTo = fmtDate(firstNonEmpty(session.assessmentDateTo, form.assessmentDateTo));

  const practiceName = firstNonEmpty(form.practiceName, form.practice);
  const location = firstNonEmpty(session.location);
  const sessionStartTime = firstNonEmpty(session.sessionStartTime);
  const sessionEndTime = firstNonEmpty(session.sessionEndTime);

  const interpreterUsed = formatBool(session.interpreterUsed);
  const interpreterName = firstNonEmpty(session.interpreterName);
  const commentSection = firstNonEmpty(session.commentSection);

  const isPrinted = formatBool(form.isPrinted);
  const isChecked = formatBool(form.isChecked);
  const isAuditReady = formatBool(form.isAuditReady);
  const isLocked = formatBool(form.formlock);

  const claimId = firstNonEmpty(form.claimId, session.claimId) || "—";
  const attachedDocument = patientDocumentUrl ? (
    <a href={patientDocumentUrl} target="_blank" rel="noopener noreferrer">
      View Document
    </a>
  ) : (
    "—"
  );

  const clientSignatureDate = fmtDateTime(
    firstNonEmpty(form.patientSignatureDATE, session.patientSignatureDATE)
  );

  const interpreterSignature = firstNonEmpty(
    form.messageinternSign,
    form.messageInternSign
  );
  const interpreterSignatureDate = fmtDateTime(form.staffsignatureDATE);

  const mhpSignature = firstNonEmpty(
    form.messageSupervisorSign,
    form.messageSupervisorSignature
  );
  const mhpSignatureDate = fmtDateTime(form.supervisorSignatureDATE);

  const mhProfessionalSignature = firstNonEmpty(
    form.messagetreatmentdirector,
    form.messageTreatmentDirector
  );
  const mhProfessionalSignatureDate = fmtDateTime(form.directorDate);

  const acknowledgementText =
    "I hereby verify that the information provided above is accurate, and the visit was conducted as described.";

  return (
    <>
      <Paper
        sx={{
          p: 0,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: 4,
            pb: 0,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ position: "absolute", top: 16, left: 16 }}
          >
            <strong>DOS:</strong> {dosVal}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
              color: "#000",
            }}
          >
            {headerTitle}
          </Typography>

          {practiceName && (
            <Typography
              variant="subtitle2"
              sx={{
                position: "absolute",
                top: 40,
                left: "50%",
                transform: "translateX(-50%)",
                color: "#555",
              }}
            >
              {practiceName}
            </Typography>
          )}

          <Box sx={{ height: 40 }} />
          <Divider />
        </Box>

        {/* Patient Info Strip */}
        <Box sx={{ px: 4 }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={4}>
              <Typography variant="body2">
                <strong>File #:</strong> {fileNo}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="body2">
                <strong>Client Name:</strong> {clientName}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body2">
                <strong>DOB:</strong> {dobVal}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 3 }} />
        </Box>

        {/* Main Table Content */}
        <Box sx={{ px: 4, pb: 4, maxWidth: "100%", overflowX: "hidden" }}>
          <table
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
              fontSize: "12pt",
              background: "white",
              wordWrap: "break-word",
            }}
          >
            <tbody>
              {/* Session Details */}
              <Section title="Session Details" />
              <Row label="Client Name" value={clientName || "—"} />
              <Row
                label="Client Account #"
                value={
                  firstNonEmpty(
                    form.patientAccount,
                    form.accountNo,
                    session.PatientAccountClient
                  ) || "—"
                }
              />
              <Row label="Assessment Date" value={assessmentDate} />
              <Row label="Assessment Date To" value={assessmentDateTo} />
              <Row label="Session Start Time" value={sessionStartTime || "—"} />
              <Row label="Session End Time" value={sessionEndTime || "—"} />
              <Row label="Location" value={location || "—"} />

              {/* Status */}
              <Section title="Form Status" />
              <Row label="Printed and Filed" value={isPrinted} />
              <Row label="Inactive" value={isChecked} />
              <Row label="Audit Ready" value={isAuditReady} />
              <Row label="Locked" value={isLocked} />

              {/* Documents & Claims */}
              <Section title="Documents and Claims" />
              <Row label="Attached Document" value={attachedDocument} />
              <Row label="Claim ID" value={claimId || "—"} />

              {/* Interpreter and Comments */}
              <Section title="Interpreter and Comments" />
              <Row label="Interpreter Used" value={interpreterUsed} />
              <Row label="Interpreter’s Name" value={interpreterName || "—"} />
              <Row label="Comment Section" value={commentSection || "—"} />

              {/* Acknowledgement and Signatures */}
              <Section title="Acknowledgement" />
              <Row label="Acknowledgement" value={acknowledgementText} />

              <Section title="Signatures" />

              {/* Client Signature with drawing popup */}
              <tr style={{ background: "white" }}>
                <td
                  style={{
                    width: "30%",
                    padding: "6px",
                    border: "1px solid #ddd",
                    fontWeight: 600,
                    verticalAlign: "top",
                  }}
                >
                  Client Signature
                </td>
                <td
                  style={{
                    width: "70%",
                    padding: "6px",
                    border: "1px solid #ddd",
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    verticalAlign: "top",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {patientSigLink ? (
                      <>
                        <img
                          src={patientSigLink}
                          alt="Client Signature"
                          style={{
                            display: "block",
                            width: 200,
                            height: 80,
                            border: "2px solid #000",
                            backgroundColor: "#f5f5f5",
                            padding: 10,
                            borderRadius: 8,
                            objectFit: "contain",
                          }}
                        />
                        <Tooltip title="Edit Signature" arrow>
                          <Button
                            onClick={openClientModal}
                            variant="outlined"
                            size="small"
                            style={{ minWidth: "32px", padding: "4px" }}
                            aria-label="Edit Signature"
                          >
                            <EditIcon />
                          </Button>
                        </Tooltip>

                        <Tooltip title="Remove Signature" arrow>
                          <Button
                            onClick={handleDeleteClientSignature}
                            style={{ minWidth: "32px", padding: "4px" }}
                            aria-label="Remove Signature"
                          >
                            <RemoveCircleOutlineIcon style={{ color: "red" }} />
                          </Button>
                        </Tooltip>
                      </>
                    ) : (
                      <Link component="button" onClick={openClientModal}>
                        Open signing window
                      </Link>
                    )}
                  </Box>
                </td>
              </tr>

              <Row
                label="Client Signature Date/Time"
                value={clientSignatureDate}
              />

              {/* Read Only Staff Signatures */}
              <Row
                label="Interpreter Signature"
                value={interpreterSignature || "—"}
              />
              <Row
                label="Interpreter Signature Date/Time"
                value={interpreterSignatureDate}
              />

              <Row label="MHP/RW Signature" value={mhpSignature || "—"} />
              <Row
                label="MHP/RW Signature Date/Time"
                value={mhpSignatureDate}
              />

              <Row
                label="Mental Health Professional Signature"
                value={mhProfessionalSignature || "—"}
              />
              <Row
                label="Mental Health Professional Signature Date/Time"
                value={mhProfessionalSignatureDate}
              />
            </tbody>
          </table>

          {/* Save Form button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Client Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
             
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientSessionVerificationPreview;